import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import type { RoomState } from '@majority-report/shared';
import { useGame } from '../net/GameProvider';
import { Hearts } from '../components/Hearts';
import { PlayerChip } from '../components/PlayerChip';
import { Button, Card, Heading, Stack, Subtle, Title } from '../components/ui';
import { pop } from '../styles/GlobalStyle';

const cheer = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50%      { transform: translateY(-14px) rotate(3deg); }
`;

const Trophy = styled.div`
  font-size: clamp(3.2rem, 14vw, 5rem);
  text-align: center;
  line-height: 1;
  animation: ${cheer} 2.2s ease-in-out infinite;
`;

const Podium = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

const Winner = styled.div<{ $index: number }>`
  animation: ${pop} 500ms ${({ theme }) => theme.transitions.bounce} both;
  animation-delay: ${({ $index }) => 120 + $index * 160}ms;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 22px 26px;
  min-width: 150px;
  border-radius: ${({ theme }) => theme.radii.lg};
  background: linear-gradient(160deg, #fff6d9, #ffe9f4);
  box-shadow: ${({ theme }) => theme.shadows.puffLg};
  border: 3px solid ${({ theme }) => theme.colors.lemon};
`;

const WinnerFace = styled.div`
  font-size: 52px;
  line-height: 1;
`;

const WinnerName = styled.div`
  font-weight: 800;
  font-size: 1.15rem;
  text-align: center;
`;

const Roster = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 9px;
`;

export function GameOverScreen({ state }: { state: RoomState }) {
  const { t } = useTranslation();
  const { actions, playerId, isHost } = useGame();

  const winners = state.players.filter((p) => state.winnerIds.includes(p.id));
  // Usually two finalists, but a double knockout can leave one or nobody.
  const title =
    winners.length >= 2
      ? t('gameover.title')
      : winners.length === 1
        ? t('gameover.soloTitle')
        : t('gameover.nobodyTitle');

  return (
    <>
      <Trophy aria-hidden="true">{winners.length > 0 ? '🏆' : '🫧'}</Trophy>
      <Title>{title}</Title>

      {winners.length > 0 && (
        <Podium>
          {winners.map((player, index) => (
            <Winner key={player.id} $index={index}>
              <WinnerFace aria-hidden="true">{player.avatar}</WinnerFace>
              <WinnerName>{player.nickname}</WinnerName>
              <Hearts hearts={player.hearts} size={18} />
              <Subtle>{t('gameover.roundsPlayed', { count: state.round })}</Subtle>
            </Winner>
          ))}
        </Podium>
      )}

      <Card>
        <Stack $gap="14px">
          <Heading>{t('lobby.playersTitle')}</Heading>
          <Roster>
            {state.players.map((player) => (
              <PlayerChip
                key={player.id}
                player={player}
                isYou={player.id === playerId}
                isHost={player.id === state.hostId}
                maxHearts={state.settings.startingHearts}
              />
            ))}
          </Roster>
        </Stack>
      </Card>

      {/* Leaving lives in the top bar, available on every screen — no duplicate here. */}
      {isHost ? (
        <Button type="button" $tone="primary" $size="lg" $full onClick={actions.playAgain}>
          🔁 {t('gameover.playAgain')}
        </Button>
      ) : (
        <Subtle $center>{t('gameover.waitingForHostRestart')}</Subtle>
      )}
    </>
  );
}
